import { useState, useEffect } from 'react'
import { KudosDto, MeetingType } from '../../types'
import { useKudosForEmployee, useKudosReceivedByMe, useKudosForEmployeeInAppraisal, useAttentionPointsForEmployee } from './useKudosQueries'
import { KUDOS_CAROUSEL_PAGE_SIZE } from './util'

export const useKudosForResignedEmployeeWithCache = (
  employeeId: string,
): {
  isKudosEnabled: boolean
  employeeOptedOut: boolean
  currentKudos: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [cachedKudos, setCachedKudos] = useState<Map<number, KudosDto[]>>(new Map())
  const [totalPages, setTotalPages] = useState(0)
  const [isKudosEnabled, setIsKudosEnabled] = useState<boolean>(false)
  const [employeeOptedOut, setEmployeeOptedOut] = useState<boolean>(true)

  const shouldFetch = !cachedKudos.has(currentPage)

  const {
    data: kudosData,
    loading,
    error,
  } = useKudosForEmployee(
    employeeId,
    {
      page: currentPage,
      limit: KUDOS_CAROUSEL_PAGE_SIZE,
    },
    true,
    !shouldFetch,
  )

  useEffect(() => {
    if (kudosData?.getKudosForEmployee && shouldFetch) {
      const newKudos = kudosData.getKudosForEmployee.kudosPagination.kudos

      setCachedKudos((prev) => new Map(prev).set(currentPage, newKudos))
      setEmployeeOptedOut(kudosData.getKudosForEmployee.employeeOptedOut)
      setIsKudosEnabled(kudosData.getKudosForEmployee.isKudosEnabled)

      if (kudosData.getKudosForEmployee.kudosPagination.totalPages) {
        setTotalPages(kudosData.getKudosForEmployee.kudosPagination.totalPages)
      }
    }
  }, [kudosData, currentPage, shouldFetch])

  const getCurrentPageKudos = () => {
    return cachedKudos.get(currentPage) || []
  }

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  return {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos: getCurrentPageKudos(),
    currentPage,
    totalPages,
    loading: loading && shouldFetch,
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
  }
}

export const useAttentionPointsForResignedEmployeeWithCache = (
  employeeId: string,
  isUserHr: boolean,
): {
  currentAttentionPoints: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [cachedAttentionPoints, setCachedAttentionPoints] = useState<Map<number, KudosDto[]>>(new Map())
  const [totalPages, setTotalPages] = useState(0)

  const shouldFetch = !cachedAttentionPoints.has(currentPage) && isUserHr

  const {
    data: attentionPointsData,
    loading,
    error,
  } = useAttentionPointsForEmployee(
    employeeId,
    {
      page: currentPage,
      limit: KUDOS_CAROUSEL_PAGE_SIZE,
    },
    true,
    !shouldFetch,
  )

  useEffect(() => {
    if (attentionPointsData?.getAttentionPointsForEmployee && shouldFetch) {
      const newAttentionPoints = attentionPointsData.getAttentionPointsForEmployee.kudos

      setCachedAttentionPoints((prev) => new Map(prev).set(currentPage, newAttentionPoints))

      if (attentionPointsData.getAttentionPointsForEmployee.totalPages) {
        setTotalPages(attentionPointsData.getAttentionPointsForEmployee.totalPages)
      }
    }
  }, [attentionPointsData, currentPage, shouldFetch])

  const getCurrentPageAttentionPoints = () => {
    return cachedAttentionPoints.get(currentPage) || []
  }

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  return {
    currentAttentionPoints: getCurrentPageAttentionPoints(),
    currentPage,
    totalPages,
    loading: loading && shouldFetch,
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
  }
}

export const useMyKudos = (): {
  isKudosEnabled: boolean
  employeeOptedOut: boolean
  currentKudos: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  onKudosOptOutValueUpdated: () => void
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [currentKudos, setCurrentKudos] = useState<KudosDto[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [isKudosEnabled, setIsKudosEnabled] = useState<boolean>(false)
  const [employeeOptedOut, setEmployeeOptedOut] = useState<boolean>(true)
  const [isRefetching, setIsRefetching] = useState(false)

  const {
    data: kudosData,
    loading,
    error,
  } = useKudosReceivedByMe({
    page: currentPage,
    limit: KUDOS_CAROUSEL_PAGE_SIZE,
  })

  useEffect(() => {
    if (kudosData?.getKudosReceivedByMe) {
      setCurrentKudos(kudosData.getKudosReceivedByMe.kudosPagination.kudos)
      setEmployeeOptedOut(kudosData.getKudosReceivedByMe.employeeOptedOut)
      setIsKudosEnabled(kudosData.getKudosReceivedByMe.isKudosEnabled)
      setTotalPages(kudosData.getKudosReceivedByMe.kudosPagination.totalPages)
      setIsRefetching(false) // Reset refetching state when data arrives
    }
  }, [kudosData])

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  const onKudosOptOutValueUpdated = () => {
    setCurrentPage(0)
    setCurrentKudos([])
    setTotalPages(0)
    setIsRefetching(true) // Show loading during refetch
  }

  return {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: loading || isRefetching,
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
    onKudosOptOutValueUpdated,
  }
}

export const useKudosForEmployeeInOverviewStep = (
  employeeId: string,
  meetingType: MeetingType,
  appraisalSeasonYear: number,
): {
  isKudosEnabled: boolean
  employeeOptedOut: boolean
  currentKudos: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  onCreateKudos: () => void
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [currentKudos, setCurrentKudos] = useState<KudosDto[]>([])
  const [isKudosEnabled, setIsKudosEnabled] = useState<boolean>(false)
  const [employeeOptedOut, setEmployeeOptedOut] = useState<boolean>(true)
  const [totalPages, setTotalPages] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)

  const {
    data: kudosData,
    loading,
    error,
  } = useKudosForEmployeeInAppraisal(employeeId, {
    pagination: {
      page: currentPage,
      limit: KUDOS_CAROUSEL_PAGE_SIZE,
    },
    meetingType: meetingType,
    appraisalSeasonYear,
  })

  // Update states when data changes
  useEffect(() => {
    if (kudosData?.getKudosForEmployeeInAppraisal) {
      setCurrentKudos(kudosData.getKudosForEmployeeInAppraisal.kudosPagination.kudos || [])
      setEmployeeOptedOut(kudosData.getKudosForEmployeeInAppraisal.employeeOptedOut)
      setIsKudosEnabled(kudosData.getKudosForEmployeeInAppraisal.isKudosEnabled)
      setTotalPages(kudosData.getKudosForEmployeeInAppraisal.kudosPagination.totalPages || 0)
      setIsRefetching(false) // Reset refetching state when data arrives
    }
  }, [kudosData])

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  const onCreateKudos = () => {
    setCurrentPage(0)
    setCurrentKudos([])
    setTotalPages(0)
    setIsRefetching(true) // Show loading during refetch
  }

  return {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: loading || isRefetching, // Show loading during refetch or normal loading
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
    onCreateKudos,
  }
}

export const useKudosForEmployeeInPublicProfile = (
  employeeId: string,
): {
  isKudosEnabled: boolean
  employeeOptedOut: boolean
  currentKudos: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  onCreateKudos: () => void
  onKudosOptOutValueUpdated: () => void
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [currentKudos, setCurrentKudos] = useState<KudosDto[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [isKudosEnabled, setIsKudosEnabled] = useState<boolean>(false)
  const [employeeOptedOut, setEmployeeOptedOut] = useState<boolean>(true)
  const [isRefetching, setIsRefetching] = useState(false)

  const {
    data: kudosData,
    loading,
    error,
  } = useKudosForEmployee(
    employeeId,
    {
      page: currentPage,
      limit: KUDOS_CAROUSEL_PAGE_SIZE,
    },
    false,
    false,
  )

  // Update states when data changes
  useEffect(() => {
    if (kudosData?.getKudosForEmployee) {
      setCurrentKudos(kudosData.getKudosForEmployee.kudosPagination.kudos || [])
      setEmployeeOptedOut(kudosData.getKudosForEmployee.employeeOptedOut)
      setIsKudosEnabled(kudosData.getKudosForEmployee.isKudosEnabled)
      setTotalPages(kudosData.getKudosForEmployee.kudosPagination.totalPages || 0)
      setIsRefetching(false) // Reset refetching state when data arrives
    }
  }, [kudosData])

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  const onCreateKudos = () => {
    setCurrentPage(0)
    setCurrentKudos([])
    setTotalPages(0)
    setIsRefetching(true) // Show loading during refetch
  }

  const onKudosOptOutValueUpdated = () => {
    setCurrentPage(0)
    setCurrentKudos([])
    setTotalPages(0)
    setIsRefetching(true)
  }

  return {
    isKudosEnabled,
    employeeOptedOut,
    currentKudos,
    currentPage,
    totalPages,
    loading: loading || isRefetching, // Show loading during refetch or normal loading
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
    onCreateKudos,
    onKudosOptOutValueUpdated,
  }
}

export const useAttentionPointsForEmployeeInPublicProfile = (
  employeeId: string,
  isUserHr: boolean,
): {
  currentAttentionPoints: KudosDto[]
  currentPage: number
  totalPages: number
  loading: boolean
  error: any
  goToNextPage: () => void
  goToPreviousPage: () => void
  hasNextPage: boolean
  hasPreviousPage: boolean
  onCreateAttentionPoint: () => void
} => {
  const [currentPage, setCurrentPage] = useState(0)
  const [currentAttentionPoints, setCurrentAttentionPoints] = useState<KudosDto[]>([])
  const [totalPages, setTotalPages] = useState(0)
  const [isRefetching, setIsRefetching] = useState(false)

  const {
    data: attentionPointsData,
    loading,
    error,
  } = useAttentionPointsForEmployee(
    employeeId,
    {
      page: currentPage,
      limit: KUDOS_CAROUSEL_PAGE_SIZE,
    },
    false,
    !isUserHr,
  )

  // Update states when data changes
  useEffect(() => {
    if (attentionPointsData?.getAttentionPointsForEmployee) {
      setCurrentAttentionPoints(attentionPointsData.getAttentionPointsForEmployee.kudos || [])
      setTotalPages(attentionPointsData.getAttentionPointsForEmployee.totalPages || 0)
      setIsRefetching(false) // Reset refetching state when data arrives
    }
  }, [attentionPointsData])

  const goToPage = (page: number) => {
    setCurrentPage(page)
  }

  const goToNextPage = () => {
    if (currentPage < totalPages - 1) {
      goToPage(currentPage + 1)
    }
  }

  const goToPreviousPage = () => {
    if (currentPage > 0) {
      goToPage(currentPage - 1)
    }
  }

  const onCreateAttentionPoint = () => {
    setCurrentPage(0)
    setCurrentAttentionPoints([])
    setTotalPages(0)
    setIsRefetching(true) // Show loading during refetch
  }

  return {
    currentAttentionPoints,
    currentPage,
    totalPages,
    loading: loading || isRefetching, // Show loading during refetch or normal loading
    error,
    goToNextPage,
    goToPreviousPage,
    hasNextPage: currentPage < totalPages - 1,
    hasPreviousPage: currentPage > 0,
    onCreateAttentionPoint,
  }
}
